import { Fragment, useEffect, useRef, useState, type KeyboardEvent, type MouseEvent, type PointerEvent } from 'react'
import {
  formatRelativeTime,
  type Note,
  type NoteStatus,
} from '../domain/note'
import { isReminderElapsed, reminderDisplayAt, reminderNaturalTime } from '../domain/reminder'
import type { NoteSearchResult } from '../search/noteSearch'
import { Icon } from './Icon'
import type { NoteAsset } from '../assets/assetTypes'
import { ImageAttachments } from './ImageAttachments'
import type { NoteCover } from '../screenshotCleanup/covers'
import type { UnpackedCard } from '../screenshotCleanup/card'
import { TagPrefix } from './TagLabel'
import { BrowseCardMedia, BrowseCardText } from './BrowseCardContent'
import { noteCardTransitionName } from '../utils/noteCardTransition'
import { linkDetailsLoading, type LinkMetadataStatus } from '../links/linkMetadata'
import { APP_SETTINGS } from '../config/appSettings'
import type { SearchLayout } from '../search/layoutPreference'
import {
  DEFAULT_BROWSE_PRESENTATION,
  type BrowseModePresentation,
} from '../search/browsePresentation'
import { buildBrowseCardModel } from '../search/browseCardModel'
import { cardRecipe } from '../search/cardRecipe'

interface SearchResultCardProps {
  previewOnly?: boolean
  active?: boolean
  copied: boolean
  id?: string
  note: Note
  result: NoteSearchResult
  onCopy: (note: Note) => void
  onBump: (note: Note) => void
  onOpen: (note: Note, matchTerm?: string) => void
  onStatusChange: (note: Note, status: NoteStatus) => void
  onSelectionStart?: (note: Note) => void
  onSelectionToggle?: (note: Note) => void
  assets?: NoteAsset[]
  /** A cleaned note's own picture, shown in place of its screenshots. */
  cover?: NoteCover | null
  /** Fetches a cover's full image: the card zooms into part of it, which the thumbnail is too small for. */
  loadFullImage?: (asset: NoteAsset) => Promise<Blob>
  /** An unpacked note's kind and title (Unpack): shown under its picture in place of the note's own title, unless the person named the note. */
  unpacked?: UnpackedCard | null
  remoteAssetUrls?: ReadonlyMap<string, string>
  expectedCount?: number
  linkMetadataStatus?: (url: Note['urls'][number]) => LinkMetadataStatus | null
  layout?: SearchLayout
  presentation?: BrowseModePresentation
  selected?: boolean
  selectionMode?: boolean
}

export function SearchResultCard({
  previewOnly = false,
  active = false,
  copied,
  id,
  note,
  result,
  onCopy,
  onBump,
  onOpen,
  onStatusChange,
  onSelectionStart,
  onSelectionToggle,
  assets = [],
  cover = null,
  loadFullImage,
  unpacked = null,
  remoteAssetUrls,
  expectedCount,
  linkMetadataStatus,
  layout = 'detailed',
  presentation = DEFAULT_BROWSE_PRESENTATION.layouts.detailed,
  selected = false,
  selectionMode = false,
}: SearchResultCardProps) {
  const longPressTimerRef = useRef<number | null>(null)
  const longPressOriginRef = useRef<{ x: number; y: number } | null>(null)
  const longPressTriggeredRef = useRef(false)
  const cardModel = buildBrowseCardModel({
    assetsCount: assets.length,
    expectedAssetCount: expectedCount ?? 0,
    layout,
    note,
    presentation,
    result,
  })
  // One card, five slots (search/cardRecipe.ts): what fills each is decided there; this renders them in their order.
  const pictureCount = Math.max(assets.length, expectedCount ?? 0)
  const [failedImages, setFailedImages] = useState<ReadonlySet<string>>(() => new Set())
  const recipe = cardRecipe({
    note,
    model: cardModel,
    layout,
    unpacked,
    pictures: pictureCount > 0 && !cover?.hidden,
    pictureCount,
    // Link rows belong to the large card only; smaller cards stay to the note's own name and words.
    maxLinks: layout === 'detailed' ? Math.min(presentation.maxLinkPreviews, APP_SETTINGS.links.searchPreviewMaxUrls) : 0,
    now: Date.now(),
    failedImages,
  })
  const subjectPending = recipe.subject ? linkDetailsLoading(recipe.subject, linkMetadataStatus ? linkMetadataStatus(recipe.subject) : undefined) : false
  const footerTags = note.userTags.slice(0, APP_SETTINGS.browse.footerTagPreviewMaxItems)
  const hiddenFooterTagCount = note.userTags.length - footerTags.length
  const reminderLabel = note.reminder ? reminderNaturalTime(note.reminder, undefined, { hourCycle: 'h23' }) : ''
  const reminderElapsed = note.reminder ? isReminderElapsed(note.reminder) : false

  const clearLongPress = () => {
    if (longPressTimerRef.current !== null) window.clearTimeout(longPressTimerRef.current)
    longPressTimerRef.current = null
    longPressOriginRef.current = null
  }
  useEffect(() => clearLongPress, [])
  const open = (event: MouseEvent<HTMLElement>) => {
    if (longPressTriggeredRef.current) {
      event.preventDefault()
      longPressTriggeredRef.current = false
      return
    }
    if (selectionMode) {
      onSelectionToggle?.(note)
      return
    }
    onOpen(note, result.matchTerms[0])
  }
  const handleKeyDown = (event: KeyboardEvent<HTMLElement>) => {
    if ((event.key !== 'Enter' && event.key !== ' ') || event.target !== event.currentTarget) return
    event.preventDefault()
    if (selectionMode) onSelectionToggle?.(note)
    else onOpen(note, result.matchTerms[0])
  }
  const startsOnAction = (target: EventTarget | null) => target instanceof Element
    && Boolean(target.closest('button, a, input, textarea, select, [contenteditable="true"]'))
  const startLongPress = (event: PointerEvent<HTMLElement>) => {
    if (selectionMode || !onSelectionStart || event.pointerType !== 'touch' || startsOnAction(event.target)) return
    longPressTriggeredRef.current = false
    longPressOriginRef.current = { x: event.clientX, y: event.clientY }
    longPressTimerRef.current = window.setTimeout(() => {
      longPressTimerRef.current = null
      longPressTriggeredRef.current = true
      onSelectionStart(note)
    }, APP_SETTINGS.interactions.searchResultLongPressMs)
  }
  const cancelForMovement = (event: PointerEvent<HTMLElement>) => {
    const origin = longPressOriginRef.current
    if (!origin) return
    const tolerance = APP_SETTINGS.interactions.searchResultLongPressMoveTolerancePx
    if (Math.hypot(event.clientX - origin.x, event.clientY - origin.y) > tolerance) clearLongPress()
  }
  const runAction = (event: MouseEvent, action: () => void) => {
    event.stopPropagation()
    action()
  }

  return (
    <article
      id={id}
      className={[
        'search-result',
        'browse-card',
        previewOnly ? 'search-result--preview-only' : '',
        active ? 'search-result--active' : '',
        selectionMode ? 'search-result--selection-mode' : '',
        selected ? 'search-result--selected' : '',
      ].filter(Boolean).join(' ')}
      data-card-layout={layout}
      data-card-media={recipe.media?.type}
      data-card-words={recipe.title || recipe.body || recipe.links.length ? '' : undefined}
      data-selected={selected || undefined}
      role={previewOnly ? undefined : "option"}
      aria-selected={previewOnly ? undefined : active}
      style={previewOnly ? undefined : { viewTransitionName: noteCardTransitionName('search', note.id) }}
      tabIndex={previewOnly ? undefined : 0}
      onClick={previewOnly ? undefined : open}
      onKeyDown={previewOnly ? undefined : handleKeyDown}
      onPointerDown={previewOnly ? undefined : startLongPress}
      onPointerMove={cancelForMovement}
      onPointerUp={clearLongPress}
      onPointerCancel={clearLongPress}
      onContextMenu={(event) => { if (longPressTriggeredRef.current) event.preventDefault() }}
    >
      {selectionMode ? (
        <span
          className={[
            'search-result__selection-indicator',
            'swap',
            selected ? 'search-result__selection-indicator--selected swap-active' : 'search-result__selection-indicator--add',
          ].join(' ')}
          aria-hidden="true"
        >
          <span className="swap-on search-result__selection-indicator-icon search-result__selection-indicator-icon--check">
            <Icon name="check" />
          </span>
          <span className="swap-off search-result__selection-indicator-icon search-result__selection-indicator-icon--add">
            <Icon name="new" />
          </span>
        </span>
      ) : null}
      <BrowseCardMedia
        recipe={recipe}
        onImageFailed={(source) => setFailedImages(current => new Set([...current, source]))}
        pictures={
          <ImageAttachments
            assets={assets}
            remoteUrls={remoteAssetUrls}
            expectedCount={expectedCount}
            compact
            compactGallery
            cover={cover}
            loadFullImage={loadFullImage}
          />
        }
      />
      <BrowseCardText
        recipe={recipe}
        terms={result.matchTerms}
        contentType={note.contentType}
        titlePending={subjectPending}
        interactive={!selectionMode}
        previewOnly={previewOnly}
      />

      <div className="search-result__footer">
        <div className="search-result__footer-meta">
          <span
            className="search-result__modified"
            aria-label={`Last edited ${formatRelativeTime(note.modifiedAt)}`}
            title={`Last edited ${new Date(note.modifiedAt).toLocaleString()}`}
          >
            <Icon name="clock" />
            <span>{formatRelativeTime(note.modifiedAt)}</span>
          </span>
          {note.reminder ? (
            <span
              className={`search-result__reminder${reminderElapsed ? ' search-result__reminder--elapsed' : ''}`}
              aria-label={`${reminderElapsed ? 'Elapsed' : 'Next'} reminder ${reminderLabel}`}
              title={`${reminderElapsed ? 'Elapsed' : 'Next'} reminder ${new Date(reminderDisplayAt(note.reminder)).toLocaleString()}`}
            >
              <Icon name="bell" />
              <span>{reminderLabel}</span>
            </span>
          ) : null}
          {note.userTags.length > 0 ? (
            <div className="search-result__tags" aria-label={`Note tags: ${note.userTags.join(', ')}`}>
              <span className="search-result__tag-list">
                <TagPrefix />{' '}
                {footerTags.map((tag, index) => {
                  const characters = Array.from(tag)
                  const prefix = characters.slice(0, APP_SETTINGS.browse.footerTagMinVisibleCharacters).join('')
                  const remainder = characters.slice(APP_SETTINGS.browse.footerTagMinVisibleCharacters).join('')
                  return (
                    <Fragment key={tag}>
                      {index > 0 ? <span className="search-result__tag-separator">, </span> : null}
                      <span className="search-result__tag" title={tag}>
                        <span className="search-result__tag-prefix">{prefix}</span>
                        {remainder ? <span className="search-result__tag-remainder">{remainder}</span> : null}
                      </span>
                    </Fragment>
                  )
                })}
                {hiddenFooterTagCount > 0 ? (
                  <span className="search-result__tag-overflow" title={`${hiddenFooterTagCount} more tags`}>{` (+${hiddenFooterTagCount})`}</span>
                ) : null}
              </span>
            </div>
          ) : null}
        </div>
        {!previewOnly && <div className="search-result__actions" aria-label="Note actions">
          {note.status !== 'trash' ? (
            <button
              className="btn btn-ghost btn-square search-result__action search-result__action--trash"
              type="button"
              title="Move to trash"
              aria-label="Move note to trash"
              onClick={(event) => runAction(event, () => onStatusChange(note, 'trash'))}
            >
              <Icon name="trash" />
            </button>
          ) : null}
          {onSelectionStart ? (
            <button
              className="btn btn-ghost btn-square search-result__action search-result__action--select"
              type="button"
              title="Select this note"
              aria-label="Select this note"
              onClick={(event) => runAction(event, () => onSelectionStart(note))}
            >
              <Icon name="select-multiple" />
            </button>
          ) : null}
          <button
            className="btn btn-ghost btn-square search-result__action search-result__action--bump"
            type="button"
            title="Bump to top"
            aria-label="Bump note to top"
            onClick={(event) => runAction(event, () => onBump(note))}
          >
            <Icon name="bump" />
          </button>
          {recipe.subject ? (
            <a
              className="btn btn-ghost btn-square search-result__action search-result__action--open"
              href={recipe.subject.originalUrl}
              target="_blank"
              rel="noreferrer noopener"
              title="Open the link"
              aria-label="Open the link"
              onClick={(event) => event.stopPropagation()}
            >
              <Icon name="external" />
            </a>
          ) : null}
          <button
            className="btn btn-ghost btn-square search-result__action search-result__action--copy"
            type="button"
            title={copied ? 'Copied' : 'Copy Markdown'}
            aria-label={copied ? 'Markdown copied' : 'Copy note as Markdown'}
            onClick={(event) => runAction(event, () => onCopy(note))}
          >
            <Icon name={copied ? 'check' : 'copy'} />
          </button>
        </div>}
      </div>
    </article>
  )
}
